// Resolve an iOS localization key to its text, in the currently selected
// language, for the onboarding previews. All translations are copied verbatim
// from the iOS Localizable.xcstrings (see scripts/extract-onboarding-data.js) —
// nothing here is a new/invented translation.
//
// Language is a module-level singleton (there is a single onboarding builder on
// screen). Components set it at the top of their render from a `lang` prop, so
// the plain render-helper functions can keep calling `t(key)` unchanged.
import I18N from './strings.i18n.json';

// Display metadata for the languages iOS ships. Codes not listed still work
// (they fall back to showing the code); rtl drives the preview text direction.
const LANG_META = {
  en: { native: 'English', english: 'English' },
  ar: { native: 'العربية', english: 'Arabic', rtl: true },
  bg: { native: 'Български', english: 'Bulgarian' },
  bs: { native: 'Bosanski', english: 'Bosnian' },
  cs: { native: 'Čeština', english: 'Czech' },
  de: { native: 'Deutsch', english: 'German' },
  el: { native: 'Ελληνικά', english: 'Greek' },
  es: { native: 'Español', english: 'Spanish' },
  fr: { native: 'Français', english: 'French' },
  he: { native: 'עברית', english: 'Hebrew', rtl: true },
  hr: { native: 'Hrvatski', english: 'Croatian' },
  hy: { native: 'Հայերեն', english: 'Armenian' },
  it: { native: 'Italiano', english: 'Italian' },
  ka: { native: 'ქართული', english: 'Georgian' },
  kk: { native: 'Қазақша', english: 'Kazakh' },
  'pt-BR': { native: 'Português (BR)', english: 'Portuguese (Brazil)' },
  ro: { native: 'Română', english: 'Romanian' },
  'ro-MD': { native: 'Română (MD)', english: 'Romanian (Moldova)' },
  ru: { native: 'Русский', english: 'Russian' },
  sk: { native: 'Slovenčina', english: 'Slovak' },
  sl: { native: 'Slovenščina', english: 'Slovenian' },
  sr: { native: 'Српски', english: 'Serbian' },
  tr: { native: 'Türkçe', english: 'Turkish' },
  uk: { native: 'Українська', english: 'Ukrainian' },
  uz: { native: 'Oʻzbekcha', english: 'Uzbek' },
  'zh-Hans': { native: '简体中文', english: 'Chinese (Simplified)' },
};

const EN = I18N.en || {};
const TOTAL = Object.keys(EN).length;
const STORAGE_KEY = 'kkal_onboarding_lang';

// Translation coverage vs. English — computed once (the snapshot is static).
function computeCoverage(code) {
  const table = I18N[code] || {};
  let translated = 0;
  for (const k in EN) {
    if (Object.prototype.hasOwnProperty.call(EN, k) && table[k] != null && table[k] !== '') translated++;
  }
  return { translated, total: TOTAL, missing: TOTAL - translated };
}

// Languages actually present in the snapshot — English first, then A→Z by name.
export const LANGUAGES = Object.keys(I18N)
  .map((code) => {
    const meta = LANG_META[code] || { native: code, english: code };
    return { code, native: meta.native, english: meta.english, rtl: !!meta.rtl, coverage: computeCoverage(code) };
  })
  .sort((a, b) => {
    if (a.code === 'en') return -1;
    if (b.code === 'en') return 1;
    return a.english.localeCompare(b.english);
  });

function isKnownLang(code) {
  return !!(code && I18N[code]);
}

function readStoredLang() {
  try {
    const s = localStorage.getItem(STORAGE_KEY);
    if (isKnownLang(s)) return s;
  } catch (e) {
    /* private mode / no storage */
  }
  return 'en';
}

let CURRENT = readStoredLang();

// Set the active preview language (no persistence — safe to call during render).
export function setActiveLang(code) {
  if (isKnownLang(code)) CURRENT = code;
}

export function getActiveLang() {
  return CURRENT;
}

// Set the language AND remember the choice across reloads (call from handlers).
export function persistLang(code) {
  setActiveLang(code);
  try {
    localStorage.setItem(STORAGE_KEY, CURRENT);
  } catch (e) {
    /* non-fatal */
  }
}

export function isRTL(code) {
  const l = LANGUAGES.find((x) => x.code === (code || CURRENT));
  return !!(l && l.rtl);
}

// key → text in `lang` (default: active language). Falls back to English, then
// to the raw key so untranslated entries are still visible in the preview.
export function t(key, lang) {
  if (key == null || key === '') return '';
  const table = I18N[lang || CURRENT] || EN;
  const v = table[key];
  if (v != null && v !== '') return v;
  const en = EN[key];
  return en != null && en !== '' ? en : key;
}

// Precomputed coverage lookup by code, for the picker UI.
export function coverageFor(code) {
  const l = LANGUAGES.find((x) => x.code === code);
  return l ? l.coverage : { translated: 0, total: TOTAL, missing: TOTAL };
}

export default I18N;
